/**
 * cart-subscription-min.js
 * Enforces a minimum total quantity for subscribed products across their
 * variant line items in the cart (page + drawer).
 *
 * For example, a subscribed product with a minimum of 10 cannot have its
 * combined quantity reduced below 10 — 4 of one size + 6 of another is fine,
 * but reducing either line further (or removing it) while that would leave the
 * product total below 10 is blocked.
 *
 * The minimum per product is read from the `harveys.subscribe_min_qty`
 * product metafield (default 10).
 */
(function () {
  function getLines(scope) {
    return [...(scope || document).querySelectorAll('input[data-hw-sub-product]')].map((input) => ({
      input,
      productId: input.dataset.hwSubProduct,
      min: Math.max(parseInt(input.dataset.hwSubMin, 10) || 1, 1),
    }));
  }

  function applyConstraints(scope) {
    const lines = getLines(scope);
    if (!lines.length) return;

    const groups = {};
    lines.forEach((line) => {
      if (!groups[line.productId]) groups[line.productId] = [];
      groups[line.productId].push(line);
    });

    Object.values(groups).forEach((group) => {
      const minTotal = group[0].min;
      const total = group.reduce((sum, l) => sum + (parseInt(l.input.value, 10) || 0), 0);

      group.forEach((line) => {
        const qty = parseInt(line.input.value, 10) || 0;
        const others = total - qty;
        // A line can drop to minTotal minus the rest of the group, never below 0.
        const floor = Math.max(0, minTotal - others);
        line.input.min = String(floor);

        const minus = line.input.closest('quantity-input')
          ? line.input.closest('quantity-input').querySelector('button[name="minus"]')
          : null;
        if (minus) minus.disabled = qty <= floor;

        // Explain why the quantity can't be reduced when this line is at its floor.
        const msg = line.input.closest('td.cart-item__qty')
          ? line.input.closest('td.cart-item__qty').querySelector('.hw-cart-sub-msg')
          : null;
        if (msg) msg.hidden = !(qty <= floor);
      });
    });
  }

  function clampValue(input) {
    const floor = parseInt(input.min, 10) || 0;
    const qty = parseInt(input.value, 10);
    if (!isNaN(qty) && qty < floor) {
      input.value = String(floor);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Removes every subscription line for a product. The cart re-renders after
   * each removal, so the line indices are re-queried each pass.
   */
  async function removeAllProductSubscriptionLines(productId) {
    const cartItems = document.querySelector('cart-items');
    if (!cartItems || typeof cartItems.updateQuantity !== 'function') return;

    for (let guard = 0; guard < 50; guard++) {
      const lines = getLines(document).filter((l) => l.productId === productId);
      if (!lines.length) break;
      const index = parseInt(lines[0].input.dataset.index, 10);
      try {
        await cartItems.updateQuantity(index, 0);
      } catch (e) {
        break;
      }
      await new Promise((resolve) => setTimeout(resolve, 120));
    }
  }

  function guardRemove(event) {
    const link = event.target.closest('a.js-remove-item[data-hw-sub-product]');
    if (!link) return;

    const productId = link.dataset.hwSubProduct;
    const minTotal = Math.max(parseInt(link.dataset.hwSubMin, 10) || 1, 1);
    const index = parseInt(link.dataset.index, 10);

    const lines = getLines(document).filter((l) => l.productId === productId);
    if (!lines.length) return;

    // Quantity that would remain across the other subscription lines for this
    // product if this line is removed.
    const remaining = lines.reduce((sum, l) => {
      if (parseInt(l.input.dataset.index, 10) === index) return sum;
      return sum + (parseInt(l.input.value, 10) || 0);
    }, 0);

    // Removing this line would leave a partial total below the minimum, so
    // remove all subscription lines for this product instead.
    if (remaining > 0 && remaining < minTotal) {
      event.preventDefault();
      event.stopImmediatePropagation();
      removeAllProductSubscriptionLines(productId);
    }
  }

  document.addEventListener('change', (event) => {
    if (event.target.dataset && event.target.dataset.hwSubProduct) {
      clampValue(event.target);
      applyConstraints(document);
    }
  });
  document.addEventListener('input', (event) => {
    if (event.target.dataset && event.target.dataset.hwSubProduct) {
      clampValue(event.target);
      applyConstraints(document);
    }
  });
  document.addEventListener('click', guardRemove, true);

  function init() {
    applyConstraints(document);
  }

  document.addEventListener('on:cart:refresh', init);
  document.addEventListener('on:line-item:change', () => setTimeout(init, 150));
  document.addEventListener('on:cart:add', () => setTimeout(init, 150));
  document.addEventListener('dispatch:cart-drawer:refresh', () => setTimeout(init, 150));

  // The drawer/page re-render their line items on cart updates, so re-apply
  // whenever the DOM changes.
  const observer = new MutationObserver(() => applyConstraints(document));
  observer.observe(document.body, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();